/**
 * Display metadata for formula-context keys that `BASELINE_REGISTRY` does not
 * define. `buildFormulaContext` emits structural families the registry never
 * enumerated — one FTE and one cost key per workforce role, plus the encounter
 * sub-volumes — and those keys are real, resolvable organization values. This
 * module gives them a label and a unit so they can be advertised to model
 * generation and rendered in the assumptions rail (ELI-1068).
 *
 * Registry definitions always win; consult this module only when
 * `getBaselineDefinition` misses.
 */
import {
  parseWorkforceContextKey,
  WORKFORCE_CONTEXT_PREFIX,
} from "../profile/workforce";
import { getWorkforceRoleLabel } from "../profile/workforce-registry";
import type { BaselineDefinition } from "./registry";

export interface StructuralBaselineMeta {
  label: string;
  unit: BaselineDefinition["unit"];
}

/**
 * Encounter volumes emitted by `buildFormulaContext` from
 * `profileData.encounter_volumes`. `inpatient_discharges` and
 * `total_encounters` are listed for completeness even though the registry
 * defines them — a registry hit short-circuits before this table is consulted,
 * and listing them keeps the family readable in one place.
 */
const ENCOUNTER_VOLUME_LABELS: Readonly<Record<string, string>> = {
  total_encounters: "Total Encounters",
  inpatient_discharges: "Inpatient Discharges",
  outpatient_visits: "Outpatient Visits",
  ed_visits: "ED Visits",
  ambulatory_visits: "Ambulatory Visits",
  radiology_encounters: "Radiology Encounters",
  surgical_cases: "Surgical Cases",
};

/** "physician_count" -> "Physician count"; "annualRevenue" -> "Annual revenue". */
export function humanizeBaselineKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

/**
 * Label + unit for a structural context key, or undefined when the key belongs
 * to no known family. `roleLabels` maps a workforce role key to its display
 * label (built by the caller from `summarizeWorkforce`) so user-added custom
 * roles get their own name instead of the raw slug.
 */
export function resolveStructuralBaselineMeta(
  key: string,
  roleLabels?: Readonly<Record<string, string>>,
): StructuralBaselineMeta | undefined {
  const encounterLabel = ENCOUNTER_VOLUME_LABELS[key];
  if (encounterLabel) return { label: encounterLabel, unit: "count" };

  const literalRole = key.startsWith(WORKFORCE_CONTEXT_PREFIX)
    ? key.slice(WORKFORCE_CONTEXT_PREFIX.length)
    : undefined;
  const workforce =
    literalRole && roleLabels && Object.hasOwn(roleLabels, literalRole)
      ? { role: literalRole, field: "ftes" as const }
      : parseWorkforceContextKey(key);
  if (!workforce) return undefined;

  const roleLabel =
    roleLabels?.[workforce.role] ?? getWorkforceRoleLabel(workforce.role);
  return workforce.field === "cost"
    ? { label: `${roleLabel} Fully Loaded Cost`, unit: "currency" }
    : { label: `${roleLabel} (FTE)`, unit: "fte" };
}
